import type { CharacterKind, Difficulty, PlayerId } from '@/components'
import type { EventBus } from '@/core/EventBus'
import type { Entity } from '@/core/ecs/Entity'
import { defineResource, type World } from '@/core/ecs/World'
import type { Rng } from '@/core/Rng'

export const EventsResource = defineResource<EventBus>('Events')
export const RngResource = defineResource<Rng>('Rng')

export type MatchPhase =
  | 'menu'
  | 'countdown'
  | 'fight'
  /** Brief window after the first fall, so a double knock-out reads as a draw. */
  | 'resolving'
  | 'roundOver'
  | 'matchOver'
  | 'paused'

/** Who is holding each slot's controls. */
export type Controller = 'human' | 'ai'

export interface MatchState {
  /** Per-slot control source; the AI writes into the same input frame. */
  controllers: Controller[]
  /** Difficulty applied to every AI slot. */
  difficulty: Difficulty
  /** Chosen body plan per slot — cosmetic only. */
  models: CharacterKind[]
  /** Nick per slot online; empty offline and for bots. */
  names: string[]
  phase: MatchPhase
  /** Phase the game returns to when unpausing. */
  resumePhase: MatchPhase
  /** Counts down within countdown / roundOver phases. */
  phaseTimer: number
  /** Seconds since the current round's fight began. */
  roundTime: number
  roundLimit: number
  score: number[]
  winsNeeded: number
  roundIndex: number
  roundWinner: PlayerId | null
  matchWinner: PlayerId | null
  arenaId: string
  /** Multiplier applied to the arena's authored footprint; see `scaledArena`. */
  arenaScale: number
  /**
   * Team per slot. Everyone gets their own team by default, which makes a
   * free-for-all the degenerate case of a team match rather than a separate
   * code path — score, round end and friendly fire all read this one array.
   */
  teams: number[]
  /** Entity handle per slot; -1 when not spawned. */
  players: Entity[]
  /** Who has left the platform this round. */
  fallen: boolean[]
  /** True when the last round ended with nobody standing. */
  lastRoundDraw: boolean
}

export const MatchResource = defineResource<MatchState>('Match')

/** What a match can be played to; every menu offers exactly these. */
export const WIN_TARGETS = [2, 3, 5] as const

/**
 * Who this screen belongs to — presentation only, never simulated.
 *
 * Offline it is every human at the keyboard and the pad; online it is the one
 * player at this browser, or nobody for a spectator. The HUD shows ability
 * bars for these slots, and the pad rumbles for the last of them.
 */
export interface Viewer {
  slots: PlayerId[]
  /**
   * The fighter marked as "you" on the platform: this browser's own online,
   * nobody offline, where two players sharing a screen know their colours.
   */
  marked: PlayerId | null
  /** Round trip to the game server in milliseconds, or null offline. */
  ping: number | null
}

export const ViewerResource = defineResource<Viewer>('Viewer')

/**
 * How big the world is this match, relative to the authored arenas: walking
 * scales with it linearly and launches with its square root (`launchSpeed`).
 *
 * Read from the world, never kept beside the balance table. It used to be a
 * module variable, and a server running several matches in one process let
 * whichever room started a round last decide how far every other room's hits
 * carried (docs/ONLINE.md §2.2).
 */
export function worldScale(world: World): number {
  const scale = world.resource(MatchResource).arenaScale
  return scale > 0 ? scale : 1
}

export interface Settings {
  ssao: boolean
  bloom: boolean
  staticCamera: boolean
  screenShake: number
  masterVolume: number
  sfxVolume: number
  musicVolume: number
  showDebug: boolean
}

export const SettingsResource = defineResource<Settings>('Settings')

export const DEFAULT_SETTINGS: Settings = {
  // SSAO is off by default: it costs a depth and a normal pass for a subtle
  // contact-shadow gain on an already dark scene, and it does not play well
  // with the additive effects. It stays one click away in the menu.
  ssao: false,
  bloom: true,
  staticCamera: false,
  screenShake: 1,
  masterVolume: 0.8,
  sfxVolume: 1,
  musicVolume: 0.6,
  showDebug: false,
}

/**
 * Slot colours, chosen to stay distinct on a dark arena and from each other:
 * warm orange, cold cyan, acid green, magenta, yellow, violet.
 */
export const PLAYER_COLORS: number[] = [
  0xff5a2b, 0x3ac8ff, 0x8fff3c, 0xff4fd8, 0xffd23c, 0xa07bff,
]

/**
 * A player's nick when they have one (online); otherwise human slots keep
 * player numbering and bots are numbered separately.
 */
export function fighterName(match: Pick<MatchState, 'controllers' | 'names'>, slot: number): string {
  const nick = match.names[slot]
  if (nick) return nick
  const controllers = match.controllers
  const controller = controllers[slot] ?? 'ai'
  let index = 0
  for (let i = 0; i <= slot && i < controllers.length; i++) {
    if (controllers[i] === controller) index++
  }
  return controller === 'human' ? `ИГРОК ${index}` : `БОТ ${index}`
}

/**
 * What to call the side that won.
 *
 * A team of one is just that fighter — announcing "КОМАНДА ИГРОКОВ" for a duel
 * would be absurd — so this collapses to `fighterName` in every mode except an
 * actual team match.
 */
export function teamName(match: MatchState, team: number): string {
  const members: number[] = []
  for (let i = 0; i < match.controllers.length; i++) {
    if ((match.teams[i] ?? i) === team) members.push(i)
  }
  if (members.length <= 1) return fighterName(match, members[0] ?? team)
  // A player's slot is theirs even while a bot covers a dropped line.
  return members.every((i) => match.controllers[i] === 'human' || match.names[i])
    ? 'КОМАНДА ИГРОКОВ'
    : 'КОМАНДА БОТОВ'
}

export function createMatchState(arenaId: string, winsNeeded = 3): MatchState {
  return {
    controllers: ['human', 'human'],
    difficulty: 1,
    models: ['guard', 'beetle'],
    names: [],
    phase: 'menu',
    resumePhase: 'fight',
    phaseTimer: 0,
    roundTime: 0,
    roundLimit: 90,
    score: [0, 0],
    winsNeeded,
    roundIndex: 0,
    roundWinner: null,
    matchWinner: null,
    arenaId,
    arenaScale: 1,
    teams: [0, 1],
    players: [-1, -1],
    fallen: [false, false],
    lastRoundDraw: false,
  }
}
